import React, {useContext, useEffect, useState} from "react"
import {
    BrowserRouter as Router,
    Navigate,
    Route,
} from "react-router-dom"
import "./App.css"
import {GlobalContext} from "../anwendungskonfigurator/context-provider/GlobalContextProvider.jsx"
import Content from "../anwendungskonfigurator/components/Content"
import {ActionContext} from "./context/ActionContextProvider"
import {Box, CircularProgress} from "@mui/material"
import MasterProvider from "../anwendungskonfigurator/context-provider/MasterProvider"
import {AppControls} from "./app/AppControls"
import WorkflowRoot from "../anwendungskonfigurator/components/RootComponents/WorkflowRoot.jsx"
import WorkflowStart from "../anwendungskonfigurator/components/RootComponents/WorkflowStart.jsx"
import MainContent from "./app/MainContent.jsx"
import {loadPage} from "../anwendungskonfigurator/services/configSource"
import {registerDefaultsFromResponse} from "../anwendungskonfigurator/misc/default-registry"
import {LoadingScreen} from "./app/LoadingScreen"

const CatchAllRedirect = () => {
    const {isAkJsonReady} = useContext(GlobalContext)
    return isAkJsonReady ? (
        <Navigate
            to={"/"}
            replace
        />
    ) : null
}

const ApplicationRoutes = [
    <Route
        key="catch-all"
        path="*"
        element={<CatchAllRedirect />}
    />,
]

const DEFAULT_ROUTE_AMOUNT = ApplicationRoutes.length

const PageContent = ({pageContainerId}) => {
    const {triggerAction} = useContext(ActionContext)
    const [state, setState] = useState({loading: true, config: null})

    useEffect(() => {
        let cancelled = false
        setState({loading: true, config: null})
        Promise.all([
            loadPage({containerId: pageContainerId}),
            Promise.resolve(
                triggerAction("LOAD_PAGE", {containerId: pageContainerId}),
            ),
        ])
            .then(([pageResponse]) => {
                if (cancelled) return
                registerDefaultsFromResponse(pageResponse)
                setState({loading: false, config: pageResponse?.page ?? null})
            })
            .catch((error) => {
                console.error("[PageContent] Seite konnte nicht geladen werden", error)
                if (!cancelled) setState({loading: false, config: null})
            })
        return () => {
            cancelled = true
        }
    }, [pageContainerId])

    if (state.loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "50vh",
                }}
            >
                <CircularProgress />
            </Box>
        )
    }
    if (!state.config) return <>No Content</>
    return <Content child={state.config} />
}

const applicationRoutesGen = (navigation) => {
    const children = navigation ?? []

    const pageRoutes = children
        .filter((navigationEntry) => navigationEntry?.page)
        .map((navigationEntry, index) => (
            <Route
                key={`page-${navigationEntry.path ?? index}`}
                path={navigationEntry.path}
                element={
                    <PageContent pageContainerId={navigationEntry.page.id} />
                }
            />
        ))

    const workflowRoutes = children
        .filter(
            (navigationEntry) =>
                navigationEntry?.identifierWorkflow && navigationEntry?.path,
        )
        .flatMap((navigationEntry, index) => [
            <Route
                key={`workflow-start-${navigationEntry.path ?? index}`}
                path={navigationEntry.path}
                element={
                    <WorkflowStart
                        workflowId={navigationEntry.identifierWorkflow}
                        basePath={navigationEntry.path}
                    />
                }
            />,
            <Route
                key={`workflow-step-${navigationEntry.path ?? index}`}
                path={`${navigationEntry.path}/:stepId`}
                element={
                    <WorkflowRoot
                        workflowId={navigationEntry.identifierWorkflow}
                    />
                }
            />,
        ])

    return [...pageRoutes, ...workflowRoutes, ...ApplicationRoutes]
}

const App = () => {
    const {navigation, rootDialogs, rootDrawers, isAkJsonReady, tid} =
        useContext(GlobalContext)
    const appRoutes = applicationRoutesGen(navigation)

    if (!isAkJsonReady) return <LoadingScreen tid={tid} />

    if (appRoutes?.length <= DEFAULT_ROUTE_AMOUNT) return <>No Pages configured</>

    return (
        <Router>
            <MasterProvider>
                <AppControls>
                    <MainContent
                        appRoutes={appRoutes}
                        dialogContainer={rootDialogs}
                        drawerContainer={rootDrawers}
                    />
                </AppControls>
            </MasterProvider>
        </Router>
    )
}

export default App
